import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  DollarSign,
  Calendar,
  Truck,
  FileText,
  PackagePlus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { customerAPI, paymentAPI, deliveryAPI, productAPI, productCategoryAPI } from '../utils/api';
import ReceiptPrint from '../components/ReceiptPrint';
import AddStockForm from '../components/AddStockForm';
import AddDeliveryForm from '../components/AddDeliveryForm';
import DeliveryReceipt from '../components/DeliveryReceipt';
import DeliveryBatchReceipt from '../components/DeliveryBatchReceipt';
import ReceiptHistory from '../components/ReceiptHistory';

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // States
  const [customer, setCustomer] = useState(null);
  const [stockEntries, setStockEntries] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [globalProducts, setGlobalProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customerStocks, setCustomerStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingStock, setSubmittingStock] = useState(false);
  const [submittingDelivery, setSubmittingDelivery] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [error, setError] = useState(null);

  // Refs
  const receiptHistoryRef = React.useRef(null);

  // Fetch global products
  const fetchGlobalProducts = async () => {
    try {
      const response = await productAPI.getAll();
      
      const transformedProducts = response.data.map(product => ({
        id: product.id,
        productName: product.name,
        name: product.name, // Both formats for compatibility
        unit: product.unit,
        description: product.description,
        category_id: product.category_id
      }));
      
      setGlobalProducts(transformedProducts);
    } catch (error) {
      toast.error('❌ Ürün kataloğu yüklenemedi.', {
        duration: 4000,
        position: 'top-right',
      });
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await productCategoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('❌ Kategoriler yüklenemedi.', {
        duration: 4000,
        position: 'top-right',
      });
    }
  };

  // Fetch customer stocks
  const fetchCustomerStocks = React.useCallback(async () => {
    try {
      const response = await customerAPI.getStocks(id);
      setCustomerStocks(response.data);
    } catch (error) {
      console.error('❌ Müşteri stokları yüklenirken hata:', error);
      setCustomerStocks([]);
    }
  }, [id]);

  // Fetch customer data
  const fetchCustomerData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const customerResponse = await customerAPI.getById(id);
      setCustomer(customerResponse.data);
      
      const stockResponse = await paymentAPI.getByCustomerId(id);
      
      // Parse products from JSON string if needed
      const parsedStockEntries = stockResponse.data.map(entry => ({
        ...entry,
        products: entry.products ? 
          (typeof entry.products === 'string' ? 
            (entry.products.trim() ? JSON.parse(entry.products) : []) : 
            (Array.isArray(entry.products) ? entry.products : [])
          ) : 
          // Local storage'dan products bilgisini al
          (() => {
            const storageKey = `stock_entry_${entry.id}_${parseInt(id)}`;
            const storedData = localStorage.getItem(storageKey);
            if (storedData) {
              try {
                const parsed = JSON.parse(storedData);
                return parsed.products || [];
              } catch (e) {
                return [];
              }
            }
            return [];
          })(),
        // Backend'den payment_date geliyorsa date olarak map et
        date: entry.date || entry.payment_date || entry.created_at || new Date().toISOString(),
        // method mapping
        method: entry.method || entry.payment_method || 'stok_girisi',
        // note mapping  
        note: entry.note || entry.notes || null
      }));
      
      setStockEntries(parsedStockEntries);
      
      const deliveriesResponse = await deliveryAPI.getByCustomerId(id);
      
      // Map delivery data to ensure consistent field names
      const mappedDeliveries = deliveriesResponse.data.map(delivery => ({
        ...delivery,
        // Map both possible field names for consistency
        productName: delivery.productName || delivery.product_name || 'Bilinmeyen Ürün',
        date: delivery.date || delivery.delivery_date || new Date().toISOString(),
        unit: delivery.unit || 'adet'  // Provide default unit if missing
      }));
      
      setDeliveries(mappedDeliveries);
      
      await fetchCustomerStocks();
      
    } catch (error) {
      setError(error.message || 'Müşteri detayları yüklenemedi');
      toast.error('❌ Müşteri detayları yüklenemedi.', {
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [id, fetchCustomerStocks]);

  useEffect(() => {
    if (id) {
      fetchCustomerData();
      fetchGlobalProducts();
      fetchCategories();
    }
  }, [id, fetchCustomerData]);

  // Calculations (simplified - no price tracking)
  const totalStockEntries = stockEntries.length;
  const totalDeliveries = deliveries.length;
  
  // Active stocks (non-zero)
  const activeStocks = customerStocks.filter(stock => stock.quantity > 0.001);
  const totalActiveStockTypes = activeStocks.length;
  const totalActiveStockQuantity = activeStocks.reduce((total, stock) => total + parseFloat(stock.quantity), 0);

  // Stock form submission handler
  const handleStockSubmit = async (stockData) => {
    setSubmittingStock(true);
    
    try {
      const response = await paymentAPI.create({
        customer_id: parseInt(id),
        amount: stockData.amount,
        payment_method: stockData.method,
        notes: stockData.note,
        products: stockData.products
      });
      
      // Backend response'ının farklı formatlarını handle et
      let paymentData;
      if (response.data.payment) {
        // Format 1: { payment: {...} }
        paymentData = response.data.payment;
      } else if (response.data.id) {
        // Format 2: direkt payment objesi
        paymentData = response.data;
      } else {
        // Format 3: farklı wrapper
        paymentData = response.data.data || response.data;
      }
      
      // Products bilgisini local storage'da sakla (geçici çözüm)
      const stockEntryKey = `stock_entry_${paymentData.id}_${parseInt(id)}`;
      const stockEntryData = {
        paymentId: paymentData.id,
        customerId: parseInt(id),
        products: stockData.products,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(stockEntryKey, JSON.stringify(stockEntryData));
      
      // Add stock entry with products to local state
      const newStockEntry = {
        id: paymentData.id || Date.now(),
        amount: paymentData.amount || stockData.amount,
        method: paymentData.payment_method || stockData.method,
        note: paymentData.notes || stockData.note,
        date: paymentData.date || new Date().toISOString(),
        products: stockData.products || []
      };
      
      setStockEntries(prev => [newStockEntry, ...prev]);
      
      // Stokları yeniden yükle
      await fetchCustomerStocks();
      
      // Stok girişi sonrası stok entry loglarını da yeniden yükle
      const updatedStockResponse = await paymentAPI.getByCustomerId(id);
      
      // Parse products from JSON string if needed
      const updatedParsedStockEntries = updatedStockResponse.data.map(entry => ({
        ...entry,
        products: entry.products ? 
          (typeof entry.products === 'string' ? 
            (entry.products.trim() ? JSON.parse(entry.products) : []) : 
            (Array.isArray(entry.products) ? entry.products : [])
          ) : 
          // Local storage'dan products bilgisini al
          (() => {
            const storageKey = `stock_entry_${entry.id}_${parseInt(id)}`;
            const storedData = localStorage.getItem(storageKey);
            if (storedData) {
              try {
                const parsed = JSON.parse(storedData);
                return parsed.products || [];
              } catch (e) {
                return [];
              }
            }
            return [];
          })(),
        // Backend'den payment_date geliyorsa date olarak map et
        date: entry.date || entry.payment_date || entry.created_at || new Date().toISOString(),
        // method mapping
        method: entry.method || entry.payment_method || 'stok_girisi',
        // note mapping  
        note: entry.note || entry.notes || null
      }));
      
      setStockEntries(updatedParsedStockEntries);
      
      // Fiş geçmişini de yenile
      if (receiptHistoryRef.current) {
        receiptHistoryRef.current.refresh();
      }
      
      // Success toast
      toast.success('✅ Stok girişi başarıyla kaydedildi!', {
        duration: 4000,
        position: 'top-right',
      });
      
    } catch (error) {
      console.error('❌ Stok girişi hatası:', error);
      toast.error('❌ Stok girişi sırasında hata oluştu.', {
        duration: 5000,
        position: 'top-right',
      });
      throw error;
    } finally {
      setSubmittingStock(false);
    }
  };

  // Delivery form submission handler
  const handleDeliverySubmit = async (deliveryData) => {
    setSubmittingDelivery(true);
    
    try {
      const newDeliveryData = {
        ...deliveryData,
        customerId: parseInt(id)
      };
      
      const response = await deliveryAPI.create(newDeliveryData);
      
      const newDelivery = response.data.delivery;
      
      // Ensure consistent field mapping for the new delivery
      const mappedNewDelivery = {
        ...newDelivery,
        productName: newDelivery.productName || newDelivery.product_name || deliveryData.productName || 'Bilinmeyen Ürün',
        date: newDelivery.date || newDelivery.delivery_date || new Date().toISOString(),
        unit: newDelivery.unit || deliveryData.unit || 'adet'
      };
      
      setDeliveries(prev => [mappedNewDelivery, ...prev]);
      
      // Stokları yeniden yükle
      await fetchCustomerStocks();
      
      // Teslimat sonrası delivery loglarını da yeniden yükle
      const updatedDeliveriesResponse = await deliveryAPI.getByCustomerId(id);
      const updatedMappedDeliveries = updatedDeliveriesResponse.data.map(delivery => ({
        ...delivery,
        productName: delivery.productName || delivery.product_name || 'Bilinmeyen Ürün',
        date: delivery.date || delivery.delivery_date || new Date().toISOString(),
        unit: delivery.unit || 'adet'
      }));
      setDeliveries(updatedMappedDeliveries);
      
      // Fiş geçmişini de yenile
      if (receiptHistoryRef.current) {
        receiptHistoryRef.current.refresh();
      }
      
      // Başarı mesajında kalan stok bilgisini göster
      if (response.data.remainingStock !== undefined) {
        toast.success(`✅ Teslimat kaydedildi! Kalan stok: ${response.data.remainingStock} ${mappedNewDelivery.unit}`, {
          duration: 5000,
          position: 'top-right',
        });
      } else {
        toast.success('✅ Teslimat kaydedildi!', {
          duration: 5000,
          position: 'top-right',
        });
      }
      
    } catch (error) {
      
      // Stok yetersizliği hatası özel olarak handle et
      if (error.response?.data?.type === 'INSUFFICIENT_STOCK') {
        toast.error(`❌ ${error.response.data.message}`, {
          duration: 6000,
          position: 'top-right',
        });
      } else {
        toast.error('❌ Teslimat kaydedilirken hata oluştu.', {
          duration: 5000,
          position: 'top-right',
        });
      }
      
      throw error;
    } finally {
      setSubmittingDelivery(false);
    }
  };

  // Teslimat seçimi fonksiyonları
  const handleDeliverySelect = (deliveryId) => {
    setSelectedDeliveries(prev => 
      prev.includes(deliveryId) 
        ? prev.filter(id => id !== deliveryId)
        : [...prev, deliveryId]
    );
  };

  const handleSelectAllDeliveries = () => {
    if (selectedDeliveries.length === deliveries.length) {
      setSelectedDeliveries([]);
    } else {
      setSelectedDeliveries(deliveries.map(d => d.id));
    }
  };

  const getSelectedDeliveriesData = () => {
    return deliveries.filter(delivery => selectedDeliveries.includes(delivery.id));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Müşteri detayları yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Hata Oluştu</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Sayfayı Yenile
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              Anasayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Müşteri Bulunamadı</h2>
          <p className="text-gray-600 mb-4">Aradığınız müşteri bulunamadı.</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Anasayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mr-4 bg-white rounded-apple px-4 py-2 shadow-sm border"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Geri Dön</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{customer.name}</h1>
            <p className="text-gray-600">Müşteri İşlemleri</p>
          </div>
        </div>

        {/* Basit Özet Kartları */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-500 text-white rounded-apple p-6 text-center">
            <div className="text-3xl font-bold">{totalStockEntries}</div>
            <div className="text-sm opacity-90">Stok Girişi</div>
          </div>
          <div className="bg-green-500 text-white rounded-apple p-6 text-center">
            <div className="text-3xl font-bold">{totalDeliveries}</div>
            <div className="text-sm opacity-90">Teslimat</div>
          </div>
          <div className="bg-amber-500 text-white rounded-apple p-6 text-center">
            <div className="text-3xl font-bold">{totalActiveStockTypes}</div>
            <div className="text-sm opacity-90">Stok Çeşidi</div>
          </div>
          <div className="bg-purple-500 text-white rounded-apple p-6 text-center">
            <div className="text-3xl font-bold">{totalActiveStockQuantity.toFixed(0)}</div>
            <div className="text-sm opacity-90">Toplam Miktar</div>
          </div>
        </div>

        {/* Basit Tab Navigation */}
        <div className="bg-white rounded-apple shadow-lg border border-gray-200 mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 p-4 bg-gray-100 rounded-t-apple">
            <button
              onClick={() => setActiveTab('stock')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'stock'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <PackagePlus className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Stok Gir</div>
            </button>
            <button
              onClick={() => setActiveTab('delivery')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'delivery'
                  ? 'bg-green-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Truck className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Teslimat</div>
            </button>
            <button
              onClick={() => setActiveTab('currentStocks')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'currentStocks'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DollarSign className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Stoklar</div>
            </button>
            <button
              onClick={() => setActiveTab('stockLogs')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'stockLogs'
                  ? 'bg-indigo-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Stok Logları</div>
            </button>
            <button
              onClick={() => setActiveTab('deliveries')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'deliveries'
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Truck className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Teslimatlar</div>
            </button>
            <button
              onClick={() => setActiveTab('receipts')}
              className={`py-4 px-3 rounded-apple text-center font-medium transition-all duration-200 ${
                activeTab === 'receipts'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm">Fişler</div>
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {/* Stok Girişi Tab */}
            {activeTab === 'stock' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <PackagePlus className="w-7 h-7 mr-3 text-blue-500" />
                  Stok Girişi
                </h2>
                <div className="bg-blue-50 rounded-apple p-6">
                  <AddStockForm 
                    onSubmit={handleStockSubmit}
                    loading={submittingStock}
                    globalProducts={globalProducts}
                    categories={categories}
                  />
                </div>
              </div>
            )}
            
            {/* Yeni Teslimat Tab */}
            {activeTab === 'delivery' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Truck className="w-7 h-7 mr-3 text-green-500" />
                  Yeni Teslimat
                </h2>
                <div className="bg-green-50 rounded-apple p-6">
                  <AddDeliveryForm 
                    customerId={parseInt(id)}
                    onSubmit={handleDeliverySubmit}
                    loading={submittingDelivery}
                    availableProducts={customerStocks}
                  />
                </div>
              </div>
            )}
            
            {/* Mevcut Stoklar Tab */}
            {activeTab === 'currentStocks' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <DollarSign className="w-7 h-7 mr-3 text-emerald-500" />
                  Mevcut Stoklar ({totalActiveStockTypes})
                </h2>
                
                {totalActiveStockTypes === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-apple">
                    <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Stok Yok</h3>
                    <p className="text-gray-500">Stok girişi yapın</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {customerStocks.filter(stock => stock.quantity > 0.001).map((stock) => (
                      <div key={`${stock.productId}`} className="bg-white rounded-apple p-6 border-2 border-emerald-200 hover:border-emerald-300 shadow-sm hover:shadow-md transition-all duration-200">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-emerald-100 rounded-apple flex items-center justify-center mx-auto mb-4">
                            <PackagePlus className="w-8 h-8 text-emerald-600" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{stock.productName}</h3>
                          <div className="text-3xl font-bold text-emerald-600 mb-1">
                            {parseFloat(stock.quantity).toFixed(1)}
                          </div>
                          <div className="text-lg text-emerald-700 font-medium mb-3">
                            {stock.unit}
                          </div>
                          {stock.quantity < 10 && (
                            <div className="bg-amber-100 text-amber-800 text-sm font-medium px-3 py-1 rounded-apple">
                              ⚠️ Az Kaldı
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Stok Giriş Logları Tab */}
            {activeTab === 'stockLogs' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Calendar className="w-7 h-7 mr-3 text-indigo-500" />
                  Stok Logları ({stockEntries.length})
                </h2>
                
                {stockEntries.length === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-apple">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Henüz Log Yok</h3>
                    <p className="text-gray-500">Stok girişi yaptığınızda burada görünecek</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {stockEntries
                      .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                      .map((entry) => (
                      <div key={entry.id} className="bg-white rounded-apple p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-indigo-100 rounded-apple flex items-center justify-center">
                              <span className="text-indigo-600 font-bold">#{entry.id}</span>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-gray-900">
                                Stok Girişi
                              </div>
                              <div className="text-gray-600">
                                {new Date(entry.date || entry.created_at).toLocaleDateString('tr-TR')} • 
                                {new Date(entry.date || entry.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                          
                          {entry.amount && (
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-600">{entry.amount} TL</div>
                              <div className="text-sm text-gray-500">Tutar</div>
                            </div>
                          )}
                        </div>
                        
                        {/* Ürünler */}
                        {entry.products && entry.products.length > 0 && (
                          <div className="border-t border-gray-100 pt-4">
                            <div className="text-sm font-medium text-gray-600 mb-3">Girilen Ürünler:</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {entry.products.map((product, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-apple p-3">
                                  <div className="font-medium text-gray-900">{product.name}</div>
                                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-apple text-sm font-medium">
                                    {product.quantity} {product.unit}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Not */}
                        {(entry.note || entry.notes) && (
                          <div className="border-t border-gray-100 pt-4 mt-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-apple p-3">
                              <div className="text-sm font-medium text-yellow-700 mb-1">Not:</div>
                              <div className="text-yellow-800">{entry.note || entry.notes}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Teslimatlar Tab */}
            {activeTab === 'deliveries' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Truck className="w-7 h-7 mr-3 text-purple-500" />
                    Teslimatlar ({deliveries.length})
                  </h2>
                  <div className="flex items-center space-x-3">
                    {deliveries.length > 0 && (
                      <>
                        <button
                          onClick={handleSelectAllDeliveries}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-apple font-medium transition-colors"
                        >
                          {selectedDeliveries.length === deliveries.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                        </button>
                        {selectedDeliveries.length > 0 && (
                          <div className="relative">
                            <DeliveryBatchReceipt 
                              customer={customer}
                              deliveries={getSelectedDeliveriesData()}
                              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-apple font-medium"
                            />
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                              {selectedDeliveries.length}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {deliveries.length === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-apple">
                    <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Henüz Teslimat Yok</h3>
                    <p className="text-gray-500">Teslimat yaptığınızda burada görünecek</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {deliveries
                      .sort((a, b) => new Date(b.date || b.delivery_date) - new Date(a.date || a.delivery_date))
                      .map((delivery) => (
                      <div key={delivery.id} className={`bg-white rounded-apple p-6 border-2 hover:shadow-md transition-all duration-200 ${
                        selectedDeliveries.includes(delivery.id) 
                          ? 'border-green-400 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedDeliveries.includes(delivery.id)}
                              onChange={() => handleDeliverySelect(delivery.id)}
                              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                            />
                            <div className="w-14 h-14 bg-purple-100 rounded-apple flex items-center justify-center">
                              <Truck className="w-7 h-7 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{delivery.productName}</h3>
                              <div className="text-gray-600">
                                {new Date(delivery.date).toLocaleDateString('tr-TR')} • 
                                {new Date(delivery.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-2xl font-bold text-purple-600">
                              {delivery.quantity} {delivery.unit}
                            </div>
                            <DeliveryReceipt 
                              delivery={delivery} 
                              customer={customer}
                              className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-apple text-sm"
                            />
                          </div>
                        </div>
                        
                        {delivery.note && (
                          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-apple p-3">
                            <div className="text-sm font-medium text-yellow-700 mb-1">Not:</div>
                            <div className="text-yellow-800">{delivery.note}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Fiş Geçmişi Tab */}
            {activeTab === 'receipts' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FileText className="w-7 h-7 mr-3 text-orange-500" />
                  Fiş Geçmişi
                </h2>
                <div className="bg-orange-50 rounded-apple p-6">
                  <ReceiptHistory 
                    customerId={parseInt(id)}
                    customerName={customer.name}
                    ref={receiptHistoryRef}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Print Modal */}
      <ReceiptPrint 
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        customer={customer}
        stockEntries={stockEntries}
        deliveries={deliveries}
        customerStocks={customerStocks}
      />
    </div>
  );
}

export default CustomerDetails; 